import { Copy, Database, Download, ExternalLink, FileText, Info, Layers, RotateCw, Trash2 } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import type { LayerData } from '@/atoms/layers.atom';
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@/components/ui/context-menu';
import { ProcessJobStatus } from '@/graphql/graphql';
import type { RerunChoice, RerunChoiceKey } from '@/lib/analyses/comparisonRerunChoices';
import { isComparison } from '@/lib/analyses/mergeRuns';
import { formatFileSize, type AnalysisRunFileItem, type RunFiles, type RunRow } from '@/lib/analyses/runList';

import { ComparisonOpenResultItems } from './ComparisonOpenResult';
import { ComparisonRerunContextItems } from './ComparisonRerunMenu';
import { partResultsOf, runPartsOf, usePartLabel } from './RunPartTags';

interface Props {
  row: RunRow;
  files: RunFiles;
  layersById: ReadonlyMap<string, LayerData>;
  href: string;
  canRerun: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onOpenDetails: () => void;
  onRerun: () => void;
  onOpenLayer: (layerId: string) => void;
  onDownload: (file: AnalysisRunFileItem) => void;
  onDelete: () => void;
  /** A paired comparison's re-run choices, offered instead of one Re-run. */
  rerunChoices: readonly RerunChoice[];
  onRerunChoice: (key: RerunChoiceKey) => void;
}

/** Content for the context menu rooted on each AnalysisRunRow. */
export function AnalysisRunContextMenu({
  row, files, layersById, href, canRerun, canDelete, isDeleting,
  onOpenDetails, onRerun, onOpenLayer, onDownload, onDelete, rerunChoices, onRerunChoice,
}: Props) {
  const { t } = useTranslation('project');
  const partLabel = usePartLabel();
  const { run, title } = row;
  const comparison = isComparison(run);
  const resultLayer = run.resultLayerId ? layersById.get(run.resultLayerId) : undefined;
  const hasFiles = files.pdfs.length > 0 || files.others.length > 0;
  const skipRestoreFocus = useRef(false);
  const openDialog = (action: () => void) => {
    // The sheet / dialog takes focus; closing this menu must not take it back.
    skipRestoreFocus.current = true;
    action();
  };
  const copyError = () => {
    if (!run.error) return;
    navigator.clipboard?.writeText(run.error).then(
      () => toast.success(t('analyses.details.copied')),
      () => {},
    );
  };

  return (
    <ContextMenuContent
      data-testid={`analysis-run-context-menu-${run.id}`}
      className='w-64'
      onCloseAutoFocus={(event) => {
        if (skipRestoreFocus.current) event.preventDefault();
        skipRestoreFocus.current = false;
      }}>
      <ContextMenuLabel className='truncate'>{run.name || title}</ContextMenuLabel>
      <ContextMenuSeparator />
      <ContextMenuItem data-testid='analysis-run-context-details' onSelect={() => openDialog(onOpenDetails)}>
        <Info />
        {t('analyses.details.open')}
      </ContextMenuItem>
      {comparison ? (
        <ComparisonOpenResultItems parts={partResultsOf(run, layersById)} onOpenLayer={onOpenLayer} />
      ) : (
        <ContextMenuItem
          data-testid='analysis-run-context-open-result'
          disabled={!resultLayer}
          onSelect={() => resultLayer && onOpenLayer(resultLayer.id)}>
          <ExternalLink />
          {t('analysisLauncher.runs.openResult')}
        </ContextMenuItem>
      )}
      {comparison ? (
        <ContextMenuSub>
          <ContextMenuSubTrigger
            data-testid='analysis-run-context-rerun-menu'
            disabled={!canRerun || rerunChoices.length === 0}>
            <RotateCw />
            {t('analyses.rerun')}
          </ContextMenuSubTrigger>
          <ContextMenuSubContent className='w-72'>
            <ComparisonRerunContextItems choices={rerunChoices} onChoose={(key) => openDialog(() => onRerunChoice(key))} />
          </ContextMenuSubContent>
        </ContextMenuSub>
      ) : (
        <ContextMenuItem data-testid='analysis-run-context-rerun' disabled={!canRerun} onSelect={() => openDialog(onRerun)}>
          <RotateCw />
          {t('analyses.rerun')}
        </ContextMenuItem>
      )}
      <ContextMenuSub>
        <ContextMenuSubTrigger data-testid='analysis-run-context-inputs' disabled={run.inputLayerIds.length === 0}>
          <Layers />
          {t('analyses.details.inputs')}
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className='max-h-80 max-w-80 overflow-y-auto'>
          {run.inputLayerIds.map((layerId) => {
            const layer = layersById.get(layerId);
            return (
              <ContextMenuItem
                key={layerId}
                data-testid={`analysis-run-context-input-${layerId}`}
                disabled={!layer}
                onSelect={() => onOpenLayer(layerId)}>
                <ExternalLink />
                <span className='truncate'>{layer?.name ?? `— (${t('analyses.details.deletedLayer')})`}</span>
              </ContextMenuItem>
            );
          })}
        </ContextMenuSubContent>
      </ContextMenuSub>
      <ContextMenuSub>
        <ContextMenuSubTrigger data-testid='analysis-run-context-downloads' disabled={!hasFiles}>
          <Download />
          {t('analyses.details.download')}
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className='max-h-80 w-80 overflow-y-auto'>
          {files.pdfs.length > 0 && <ContextMenuLabel>{t('analyses.files.pdfReports')}</ContextMenuLabel>}
          {files.pdfs.map((file) => (
            <DownloadItem key={file.id} file={file} onDownload={onDownload} pdf />
          ))}
          {files.pdfs.length > 0 && files.others.length > 0 && <ContextMenuSeparator />}
          {files.others.length > 0 && <ContextMenuLabel>{t('analyses.files.files')}</ContextMenuLabel>}
          {files.others.map((file) => (
            <DownloadItem key={file.id} file={file} onDownload={onDownload} />
          ))}
        </ContextMenuSubContent>
      </ContextMenuSub>
      {run.status === ProcessJobStatus.Failed && run.error && (
        <ContextMenuItem data-testid='analysis-run-context-copy-error' onSelect={copyError}>
          <Copy />
          {t('analyses.details.copyError')}
        </ContextMenuItem>
      )}
      {run.batchId && (
        <>
          <ContextMenuSeparator />
          {comparison ? (
            // One batch per part: name the part each link opens.
            runPartsOf(run).map(({ part, run: p }) => p.batchId && (
              <ContextMenuItem key={p.id} asChild>
                <Link data-testid={`analysis-run-context-data-centre-${part}`} to={`${href}/datacenter?batch=${p.batchId}`}>
                  <Database />
                  {t('analyses.details.openDataCentre')} · {partLabel(part)}
                </Link>
              </ContextMenuItem>
            ))
          ) : (
            <ContextMenuItem asChild>
              <Link data-testid='analysis-run-context-data-centre' to={`${href}/datacenter?batch=${run.batchId}`}>
                <Database />
                {t('analyses.details.openDataCentre')}
              </Link>
            </ContextMenuItem>
          )}
          {canDelete && (
            <ContextMenuItem
              data-testid='analysis-run-context-delete'
              variant='destructive'
              disabled={isDeleting}
              onSelect={() => openDialog(onDelete)}>
              <Trash2 />
              {t(comparison ? 'analyses.details.deleteComparison' : 'analyses.details.deleteRun')}
            </ContextMenuItem>
          )}
        </>
      )}
    </ContextMenuContent>
  );
}

function DownloadItem({ file, onDownload, pdf }: {
  file: AnalysisRunFileItem;
  onDownload: Props['onDownload'];
  pdf?: boolean;
}) {
  const size = formatFileSize(file.contentLength);
  return (
    <ContextMenuItem data-testid={`analysis-run-context-download-${file.id}`} onSelect={() => onDownload(file)}>
      {pdf ? <FileText className='text-rose-500 dark:text-rose-400' /> : <Download />}
      <span className='min-w-0 flex-1 truncate' title={file.filename}>{file.filename}</span>
      {size && <span className='shrink-0 text-xs tabular-nums text-slate-500 dark:text-stone-400'>{size}</span>}
    </ContextMenuItem>
  );
}
